import { useMemo, useState } from "react";

import type { PairVerdict, PairWatchSnapshot } from "@/types/api";

import { VerdictChip } from "./VerdictChip";

/**
 * DECISION GRID tab — matrix of pair x (brand/country) -> verdict chip.
 * Default view = global aggregate across the whole x2mail family
 * (brand=null). Drill-down: filter per brand / per country.
 * BELOW_VOLUME is deliberately grey (neutral), never green — design doc §6.4.
 * Ported from products/x2mail-admin/src/views/DecisionGridView.tsx, adapted
 * to the generated client's camelCase field names.
 */
export interface DecisionGridViewProps {
  snapshots: PairWatchSnapshot[];
  onCellClick: (snapshot: PairWatchSnapshot) => void;
}

export function DecisionGridView({ snapshots, onCellClick }: DecisionGridViewProps) {
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  const brands = useMemo(() => {
    const set = new Set<string | null>();
    snapshots.forEach((s) => set.add(s.brand ?? null));
    return [null, ...[...set].filter((b): b is string => b !== null).sort()];
  }, [snapshots]);

  const countries = useMemo(() => {
    const activeBrand = selectedBrand;
    const filtered = snapshots.filter((s) => (s.brand ?? null) === activeBrand);
    const set = new Set<string | null>();
    filtered.forEach((s) => set.add(s.country));
    // global first, then alphabetical
    return [null, ...[...set].filter((c): c is string => c !== null).sort()];
  }, [snapshots, selectedBrand]);

  const pairIds = useMemo(() => {
    return [...new Set(snapshots.map((s) => s.pairId))].sort();
  }, [snapshots]);

  // Build lookup: (pair_id, country) -> snapshot (for selected brand)
  const lookup = useMemo(() => {
    const map = new Map<string, PairWatchSnapshot>();
    snapshots
      .filter((s) => (s.brand ?? null) === selectedBrand)
      .forEach((s) => {
        map.set(`${s.pairId}__${s.country ?? "GLOBAL"}`, s);
      });
    return map;
  }, [snapshots, selectedBrand]);

  return (
    <div className="space-y-4">
      {/* Filtr marki */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-zinc-500">Marka:</span>
        <div className="flex flex-wrap gap-2">
          {brands.map((b) => (
            <button
              key={b ?? "__global__"}
              type="button"
              onClick={() => setSelectedBrand(b)}
              className={[
                "rounded border px-2.5 py-1 text-xs transition-colors",
                selectedBrand === b
                  ? "border-indigo-600 bg-indigo-950 text-indigo-300"
                  : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500",
              ].join(" ")}
            >
              {b ?? "Cała rodzina (global)"}
            </button>
          ))}
        </div>
      </div>

      {/* Siatka */}
      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/50">
              <th className="sticky left-0 z-10 bg-zinc-900 px-4 py-2.5 text-left text-xs font-medium text-zinc-500">
                Para
              </th>
              {countries.map((c) => (
                <th
                  key={c ?? "GLOBAL"}
                  className="px-3 py-2.5 text-center text-xs font-medium text-zinc-500"
                >
                  {c ?? "🌐 Global"}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pairIds.map((pairId) => (
              <tr key={pairId} className="border-b border-zinc-800 last:border-0">
                <td className="sticky left-0 z-10 bg-zinc-950 px-4 py-2.5">
                  <span className="font-mono text-xs text-zinc-300">{pairId}</span>
                  {selectedBrand === null && (
                    <span className="ml-2 rounded bg-zinc-800 px-1 py-0.5 text-xs text-zinc-500">
                      geo: N/A (backend global-only)
                    </span>
                  )}
                </td>
                {countries.map((country) => {
                  const key = `${pairId}__${country ?? "GLOBAL"}`;
                  const snap = lookup.get(key);
                  return (
                    <td key={country ?? "GLOBAL"} className="px-3 py-2.5 text-center">
                      {snap ? (
                        <div className="flex flex-col items-center gap-1">
                          <VerdictChip
                            verdict={snap.verdict as PairVerdict}
                            onClick={() => onCellClick(snap)}
                          />
                          <span className="text-xs text-zinc-600">
                            okno {snap.window}h · {snap.baselineLevel}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-700">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
        <span>🟢 OK — follower podąża za driverem</span>
        <span>🔴 ALERT — miga, link do szczegółów</span>
        <span>⚪ ZA MAŁO DANYCH — neutralny, nie zielony</span>
        <span>◻️ BRAK BASELINE — zbieram dane, ETA za kilka dni</span>
      </div>
    </div>
  );
}
